/**
 * datasetコマンドハンドラ
 *
 * JSON形式のDataTransformOperationを受け取り、DerivedDataSetを作成
 *
 * sql_queryタイプの場合、targetDatasetIdはSQLから自動解析されるため省略可能
 *
 * 使用例:
 * x:dataset {"type":"sql_query","query":"SELECT * FROM Iris WHERE species = 'setosa'","outputName":"Setosa"}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import type { DataTransformOperationUnion, DerivedDataSet, CrosstabValueField, DataSet } from '../../../types/dataset.types';
import { createDatasetId, createTransformOperationId, createColumnName } from '../../../types';
import { executeOperation } from '../../utils/executeOperation';
import { duckdbProxy } from '../../utils/duckdb';
import logger from '../../utils/logger';

/**
 * x:datasetコマンドで受け付けるJSON形式
 * DataTransformOperationUnionに加えて、outputNameを必須とする
 *
 * sql_queryタイプの場合、targetDatasetIdは省略可能（SQLから自動解析）
 */
interface DatasetOperationInput {
  type: string;
  targetDatasetId?: string;  // sql_queryの場合は省略可能
  outputName: string;
  // sql_query
  query?: string;
  // crosstab
  rowFields?: string[];
  columnFields?: string[];
  valueFields?: Array<{ field: string; aggregation: string; alias?: string }>;
  options?: {
    showRowTotals?: boolean;
    showColumnTotals?: boolean;
    showGrandTotal?: boolean;
  };
  // filter
  filterExpression?: unknown;
  // その他の操作固有フィールド
  [key: string]: unknown;
}

/**
 * テーブル名からデータセットIDを解決（大文字小文字無視）
 * DuckDB内部のmapTablesToDatasetsと同じロジック
 */
function findDatasetIdByTableName(
  tableName: string,
  datasets: Record<string, DataSet>
): string | null {
  const lowerTableName = tableName.toLowerCase();
  for (const [id, dataset] of Object.entries(datasets)) {
    if (dataset.name.toLowerCase() === lowerTableName) {
      return id;
    }
  }
  return null;
}

/**
 * 入力JSONからDataTransformOperationUnionを構築
 */
function buildOperation(input: DatasetOperationInput, resolvedTargetDatasetId: string): DataTransformOperationUnion | null {
  const operationId = createTransformOperationId(`op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const targetDatasetId = createDatasetId(resolvedTargetDatasetId);

  switch (input.type) {
    case 'sql_query':
      if (!input.query) return null;
      return {
        id: operationId,
        type: 'sql_query',
        targetDatasetId,
        query: input.query
      };

    case 'crosstab': {
      if (!input.rowFields || !input.columnFields) return null;
      // 文字列配列をColumnName[]に変換
      const rowFields = input.rowFields.map(f => createColumnName(f));
      const columnFields = input.columnFields.map(f => createColumnName(f));
      const valueFields: CrosstabValueField[] = (input.valueFields || []).map(vf => ({
        field: createColumnName(vf.field),
        aggregation: vf.aggregation,
        alias: vf.alias
      }));
      return {
        id: operationId,
        type: 'crosstab',
        targetDatasetId,
        rowFields,
        columnFields,
        valueFields,
        options: {
          showRowTotals: input.options?.showRowTotals ?? true,
          showColumnTotals: input.options?.showColumnTotals ?? true,
          showGrandTotal: input.options?.showGrandTotal ?? true
        }
      };
    }

    case 'filter':
      if (!input.filterExpression) return null;
      return {
        id: operationId,
        type: 'filter',
        targetDatasetId,
        filterExpression: input.filterExpression as DataTransformOperationUnion extends { type: 'filter'; filterExpression: infer F } ? F : never
      };

    default:
      return null;
  }
}

export const datasetHandler: CommandHandler = {
  name: 'dataset',
  description: 'Create a derived dataset from JSON operation specification',
  category: 'data',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false, // JSON-basedコマンドはpositionalArgsを使用するため、ここではfalse
      description: 'JSON object specifying the data transform operation'
    }
  ],
  aliases: [],
  examples: [
    'x:dataset {"type":"sql_query","query":"SELECT * FROM Iris WHERE species = \'setosa\'","outputName":"Setosa"}',
    'x:dataset {"type":"crosstab","targetDatasetId":"dataset_1","rowFields":["category"],"columnFields":["month"],"valueFields":[{"field":"amount","aggregation":"sum"}],"outputName":"pivot"}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    // JSONを取得（位置引数全体を結合）
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'JSON configuration required',
        error: {
          code: 'NO_JSON',
          message: 'No JSON configuration provided',
          suggestion: 'Provide a JSON object: x:dataset {"type":"sql_query",...}'
        }
      };
    }

    // JSONをパース
    let operationInput: DatasetOperationInput;
    try {
      operationInput = JSON.parse(jsonStr) as DatasetOperationInput;
    } catch (e) {
      logger.error('[datasetHandler] Failed to parse JSON', { jsonStr, error: e });
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    // 必須フィールドの検証
    if (!operationInput.type) {
      return {
        success: false,
        message: 'Operation type required',
        error: {
          code: 'NO_TYPE',
          message: 'Operation type is required',
          suggestion: 'Include "type" field (sql_query, crosstab, filter)'
        }
      };
    }

    // targetDatasetIdの解決
    // sql_queryタイプの場合、targetDatasetIdが未指定ならSQLから自動解析
    let resolvedTargetDatasetId = operationInput.targetDatasetId;

    if (!resolvedTargetDatasetId) {
      if (operationInput.type === 'sql_query' && operationInput.query) {
        // SQLクエリからtargetDatasetIdを自動解析
        const tableRefs = await duckdbProxy.extractTableReferencesWithLocation(operationInput.query);
        if (tableRefs.length > 0) {
          const tableName = tableRefs[0].tableName;
          resolvedTargetDatasetId = findDatasetIdByTableName(tableName, context.datasets) ?? undefined;
          if (resolvedTargetDatasetId) {
            logger.info('[datasetHandler] Auto-resolved targetDatasetId from SQL', { tableName, datasetId: resolvedTargetDatasetId });
          } else {
            return {
              success: false,
              message: `Dataset not found for table: ${tableName}`,
              error: { code: 'DATASET_NOT_FOUND', message: `No dataset found matching table name "${tableName}"` }
            };
          }
        } else {
          return {
            success: false,
            message: 'Could not extract table name from SQL',
            error: { code: 'NO_TARGET', message: 'Provide explicit "targetDatasetId" field' }
          };
        }
      } else {
        return {
          success: false,
          message: 'Target dataset ID required',
          error: { code: 'NO_TARGET', message: 'targetDatasetId is required for this operation type' }
        };
      }
    }

    if (!operationInput.outputName) {
      return {
        success: false,
        message: 'Output name required',
        error: {
          code: 'NO_OUTPUT_NAME',
          message: 'outputName is required',
          suggestion: 'Include "outputName" field for the resulting dataset'
        }
      };
    }

    // ターゲットデータセットの存在確認
    const targetDataset = context.datasets[resolvedTargetDatasetId];
    if (!targetDataset) {
      return {
        success: false,
        message: `Dataset not found: ${resolvedTargetDatasetId}`,
        error: {
          code: 'DATASET_NOT_FOUND',
          message: `Target dataset "${resolvedTargetDatasetId}" not found`,
          suggestion: 'Check available datasets'
        }
      };
    }

    // Operationを構築
    const operation = buildOperation(operationInput, resolvedTargetDatasetId);
    if (!operation) {
      return {
        success: false,
        message: `Unsupported or invalid operation type: ${operationInput.type}`,
        error: {
          code: 'INVALID_OPERATION',
          message: `Cannot build operation from type: ${operationInput.type}`,
          suggestion: 'Supported types: sql_query, crosstab, filter'
        }
      };
    }

    logger.info('[datasetHandler] Executing operation', {
      type: operation.type,
      targetDatasetId: resolvedTargetDatasetId,
      outputName: operationInput.outputName
    });

    try {
      // 操作を実行
      const result = await executeOperation(operation, context.datasets);

      // DerivedDataSetを構築
      const derivedDataset: DerivedDataSet = {
        id: createDatasetId(`derived_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`),
        name: operationInput.outputName,
        type: 'derived',
        operation,
        parentDatasetIds: result.parentDatasetIds,
        schema: result.schema,
        data: result.data,
        rowCount: result.rowCount,
        metadata: {
          createdAt: new Date().toISOString(),
          basedOn: result.parentDatasetIds
        }
      };

      // データセットを追加
      context.addDerivedDataset(derivedDataset);

      logger.info('[datasetHandler] Created derived dataset', {
        datasetId: derivedDataset.id,
        name: derivedDataset.name,
        rowCount: derivedDataset.rowCount
      });

      return {
        success: true,
        message: `Created dataset "${operationInput.outputName}" (${result.rowCount} rows)`,
        data: {
          datasetId: derivedDataset.id,
          name: derivedDataset.name,
          rowCount: result.rowCount,
          columnCount: result.schema.columns.length
        }
      };
    } catch (e) {
      logger.error('[datasetHandler] Operation failed', { error: e });
      return {
        success: false,
        message: 'Operation execution failed',
        error: {
          code: 'EXECUTION_ERROR',
          message: e instanceof Error ? e.message : String(e)
        }
      };
    }
  }
};
